import { css } from '@emotion/react'
import { Cell, Grid, Heading, VFlow } from 'bold-ui'
import { Form, FormRenderProps, PecFormApi } from 'components/form'
import { HLabel } from 'components/HLabel'
import { TableRowMenu, TableRowMenuButton } from 'components/table'
import { Fragment, useState } from 'react'
import { metaPath } from 'util/metaPath'
import { createValidator, maxLength } from 'util/validation'
import { EditableRow } from 'view/atendimentos/detail/components/EditableList'
import { ObservacaoFormGrid } from 'view/atendimentos/detail/components/ObservacaoFormGrid'

import { AntecedentesFamiliaresCiapFormModel } from '../model-antecedentes'
import { ObservacaoLabel } from './ObservacaoLabel'

const meta = metaPath<AntecedentesFamiliaresCiapFormModel>()

interface AntecedenteCiapRowProps {
  model: AntecedentesFamiliaresCiapFormModel
  canAddCid10: boolean

  onChange(values: AntecedentesFamiliaresCiapFormModel): void

  onRemove(item: AntecedentesFamiliaresCiapFormModel): void
}

const validator = createValidator<AntecedentesFamiliaresCiapFormModel>({
  observacao: [maxLength(200)],
})

type AntecedenteCiapRowState = 'default' | 'observacao'

export function AntecedenteCiapRow(props: AntecedenteCiapRowProps) {
  const { model, canAddCid10, onChange, onRemove } = props
  const [state, setState] = useState<AntecedenteCiapRowState>('default')
  const isExcluirDisabled = !!model.cid10 && !canAddCid10
  const excluirTooltip =
    'Não é permitido excluir antecedentes familiares com CID10 para profissionais que não podem registrar CID10.'

  const handleExibirLinha = () => setState('default')
  const handleEditarObservacao = () => setState('observacao')
  const handleRemove = () => onRemove(model)

  const handleSubmit = (
    values: AntecedentesFamiliaresCiapFormModel,
    formApi: PecFormApi<AntecedentesFamiliaresCiapFormModel>
  ) => {
    onChange(values)
    setTimeout(formApi.reset)
    handleExibirLinha()
  }

  const messageEditarObservacao = (item: AntecedentesFamiliaresCiapFormModel) => {
    return item.observacao ? (item.id ? 'Atualizar observações' : 'Editar observações') : 'Adicionar observações'
  }

  const classes = createStyles()

  const renderObservacaoForm = (formRenderProps: FormRenderProps<AntecedentesFamiliaresCiapFormModel>) => (
    <VFlow vSpacing={0.5} style={classes.flow}>
      <Heading level={4}>{messageEditarObservacao(model)}</Heading>
      <Grid style={classes.noMarginGrid}>
        <Cell size={6} flexGrow={1}>
          <HLabel title='CIAP 2:'>{model.ciap ? `${model.ciap.descricao} - ${model.ciap.codigo}` : '-'}</HLabel>
        </Cell>
        <Cell size={6} flexGrow={1}>
          <HLabel title='CID 10:'>{model.cid10 ? `${model.cid10.nome} - ${model.cid10.codigo}` : '-'}</HLabel>
        </Cell>
      </Grid>
      <Grid style={classes.noMarginGrid}>
        <Cell size={12}>
          <ObservacaoFormGrid
            name={meta.observacao}
            onCancel={handleExibirLinha}
            onSubmit={formRenderProps.handleSubmit}
            style={classes.observacaoNoResize}
          />
        </Cell>
      </Grid>
    </VFlow>
  )

  return (
    <EditableRow editing={state !== 'default'}>
      {state === 'default' && (
        <Fragment>
          <Grid style={classes.noMarginGrid}>
            <Cell size={4} alignSelf='center'>
              {model.ciap ? `${model.ciap.descricao} - ${model.ciap.codigo}` : '-'}
            </Cell>

            <Cell size={5} alignSelf='center'>
              {model.cid10 ? `${model.cid10.nome} - ${model.cid10.codigo}` : '-'}
            </Cell>

            <Cell size={3} alignSelf='center' style={classes.cell}>
              <TableRowMenu>
                <TableRowMenuButton
                  text={messageEditarObservacao(model)}
                  icon='chatOutline'
                  onClick={handleEditarObservacao}
                />
                <TableRowMenuButton
                  text={isExcluirDisabled ? excluirTooltip : 'Excluir'}
                  icon='trashOutline'
                  onClick={handleRemove}
                  disabled={isExcluirDisabled}
                />
              </TableRowMenu>
            </Cell>
          </Grid>

          <ObservacaoLabel observacao={model.observacao} />
        </Fragment>
      )}
      {state === 'observacao' && (
        <Form<AntecedentesFamiliaresCiapFormModel>
          render={renderObservacaoForm}
          initialValues={model}
          validate={validator}
          onSubmit={handleSubmit}
          initialValuesEqual={(oldInitial, newInitial) => oldInitial._id === newInitial._id}
        />
      )}
    </EditableRow>
  )
}

const createStyles = () => ({
  flow: css`
    padding: 1rem 0;
  `,
  noMarginGrid: css`
    margin: 0;
  `,
  cell: css`
    padding: 0.25rem 0;
    text-align: right;
  `,
  observacaoNoResize: css`
    resize: none;
    height: 4rem;
  `,
})
